import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { store } from "../../../../app/store";

// mui components
import {
	Box,
	Button,
	Divider,
	IconButton,
	Stack,
	Tab,
	Tabs,
} from "@mui/material";

// mui icons
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";

// custom components
import Heading4 from "../../../typography/heading-04";
import Heading5 from "../../../typography/heading-05";
import Body2 from "../../../typography/body-02";
import PrimaryButton from "../../../buttons/primary-button";
import SecondaryButton from "../../../buttons/secondary-button";
import CustomTable from "../../../custom-table";
import LoadingDataModal from "../../../modal/loading-data-modal";

// api slice
import { useAttachEnhancedConditionDocumentMutation } from "../../../../features/attach-enhanced-condition-document/attach-enhanced-condition-document";
import { useAttachDocumentConditionMutation } from "../../../../features/attach-document-condition/attach-document-condition";
import { useAssignDocumentAttachmentMutation } from "../../../../features/assign-document-attachment/assign-document-attachment";
import { useEnhancedConditionAttachmentUploadUrlMutation } from "../../../../features/enhanced-condition-attachment-upload-url/enhanced-condition-attachment-upload-url";
import { useAttachEnhancedConditionDocumentToUploadUrlMutation } from "../../../../features/attach-enhanced-condition-document-to-uploadurl/attach-enhanced-condition-document-to-uploadurl";
import { useEnhancedConditionUploadFileForS3FallbackMutation } from "../../../../features/enhanced-condition-upload-file-for-s3-fallback/enhanced-condition-upload-file-for-s3-fallback";
import { useStandardDocumentUploadFileForS3FallbackMutation } from "../../../../features/standard-document-upload-file-for-s3-fallback/standard-document-upload-file-for-s3-fallback";
import { useEnhancedConditionRemoveAttachmentMutation } from "../../../../features/enhanced-condition-remove-attachment/enhanced-condition-remove-attachment";
import { useEnhancedConditionRemoveConditionMutation } from "../../../../features/enhanced-condition-remove-condition/enhanced-condition-remove-condition";
import { useEnhancedConditionsTemplatesMutation } from "../../../../features/enhanced-conditions-templates/fetchEnhancedConditionsTemplatesApiSlice";
import { useEnhancedConditionsTemplateDetailsMutation } from "../../../../features/enhanced-conditions-template-details/fetchEnhancedConditionsTemplateDetailsApiSlice";
import { useEnhancedConditionsEFolderCreationMutation } from "../../../../features/create-enhanced-conditions-efolder/createEnhancedConditionsEfolderApiSlice";
import { useEnhancedConditionsDocumentsListMutation } from "../../../../features/enhanced-conditions-documents_list/fetchEnhancedConditionsDocumentsListApiSlice";
import { useEnhancedConditionsEfolderDocumentsListMutation } from "../../../../features/fetch-enhanced-conditions-efolder-documents-list/fetchEnhancedConditionsEfolderDocumentsApiSlice";
import { useEnhancedConditionsEfolderDocumentsDownloadUrlsListMutation } from "../../../../features/enhanced_conditions_documents_downloadurls/fetchEnhancedConditionsEfolderDocumentsDownloadUrlsListApiSlice";
import { useEnhancedConditionDownloadFileFromDownloadUrlMutation } from "../../../../features/enhanced_conditions_documents_downloadurls/fetchEnhancedConditionsEfolderDocumentDownloadFileFromDownloadUrlApiSlice";
import { useStandardConditionRemoveConditionMutation } from "../../../../features/preliminary-condition-remove-condition/preliminary-condition-remove-condition";
import { useUpdateFieldValuesTpoMutation } from "../../../../features/update-field-value-tpo/updateFieldsApiSlice";
import {
	getMimeTypeFromFilename,
	GENERIC_FALLBACK_CONTENT_TYPE,
	GENERIC_FALLBACK_FILE_NAME,
} from "../../../../utils/document-validation";
import {
	isRetryResponse,
	isRetryItem,
	createRetryHandledError,
	isRetryHandledError,
} from "../../../../utils/document-retry-helper";
import { useGetLoanDocumentMutation } from "../../../../features/get-loan-document/getLoanDocumentApiSlice";
import { useGetUserEmailByDocumentMutation } from "../../../../features/get-user-email-by-document/getUserEmailByDocumentApiSlice";
import { selectEnhancedConditionStatus } from "../../../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { selectImpersonatedUser } from "../../../../features/admin/impersonated-user/impersonatedUserSlice";
import { selectUserDetails } from "../../../../features/auth/authSlice";
import {
	selectSelectedOrganization,
	selectOrganizationName,
} from "../../../../features/auth/authSlice";
import { getEnhancedConditionsDocumentsList } from "../../../../app/services/enhanced-conditions-documents-list";
import { waitForPendingApiRequestsToSettle } from "../../../../features/api-request-status/apiRequestStatusSlice";
import { getTrackerMatchingConditionInfo } from "../../../custom-table/table-row/conditions-document-tracker";
import { getDocumentPipelineStatusPresentation } from "../../../../utils/document-pipeline-status";
import {
	documentTrackerUploadedByCache,
	documentTrackerUploadedByRequestCache,
	getDocumentTrackerUploadedByDocumentId,
	getDocumentTrackerUploadedByEmailFromResponse,
	getDocumentTrackerUploadedByFallbackText,
	shouldResolveDocumentTrackerUploadedBy,
} from "../../../../utils/document-tracker-uploaded-by";

// custom styles
import styles from "./index.module.scss";
import { uploadFileWithChunking } from "../../../../utils/upload-file-with-chunks";
import { uploadMultipartFile } from "../../../../utils/upload-multipart-file";

const isConflictError = (error) =>
	error?.status === 409 ||
	error?.originalStatus === 409 ||
	error?.OriginalStatus === 409 ||
	error?.status_code === 409;

const LOAN_IN_USE_FALLBACK_MESSAGE = `Document Received. \n\n Your document has been saved, but the loan is currently in use by a Logan team member. LoganConnect will automatically add the document once the loan is available.\n\n No action is needed. Please do not upload the document again.\n\n To confirm when your document is available to Logan Operations, please check the document status on the Documents screen.`;

const formatDateTime = (value) => {
	if (!value) return "NA";

	const date = DateTime.fromISO(value, { zone: "America/New_York" });
	if (!date.isValid) return "NA";

	return `${date.toFormat("MMM d, yyyy, h:mm a")} ET`;
};

const formatDateTimeEt = (value) => {
	if (!value) return "-";

	const date = DateTime.fromISO(value, { zone: "America/New_York" });
	if (!date.isValid) return "-";

	return `${date.toFormat("MMM d, yyyy, h:mm a")} ET`;
};

const formatBytesWithUnit = (value) => {
	const bytes = Number(value);

	if (!Number.isFinite(bytes) || bytes < 0) {
		return "-";
	}

	const units = ["B", "KB", "MB", "GB", "TB"];
	let size = bytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex += 1;
	}

	if (unitIndex === 0) {
		return `${Math.round(size)} B`;
	}

	const formattedSize = Number(size.toFixed(2));
	return `${formattedSize} ${units[unitIndex]}`;
};

const getAttachmentSizeBytes = (attachment) => {
	const size = Number(
		attachment?.size ??
			attachment?.fileSize ??
			attachment?.documentSize ??
			attachment?.attachmentSize ??
			attachment?.bytes ??
			0
	);

	return Number.isFinite(size) && size >= 0 ? size : 0;
};

const getAttachmentIdentifier = (attachment) =>
	String(
		attachment?.entityId ??
			attachment?.id ??
			attachment?.attachmentId ??
			attachment?.documentId ??
			attachment?.entityID ??
			attachment?.conditionId ??
			""
	).trim();

const loanDocumentDetailsCache = new Map();
const loanDocumentDetailsRequestCache = new Map();

const getBorrowerPair = (row) => {
	if (typeof row?.borrowerPair === "string" && row.borrowerPair.trim()) {
		return row.borrowerPair;
	}

	if (Array.isArray(row?.borrowers) && row.borrowers.length > 0) {
		return row.borrowers
			.map(
				(borrower) =>
					borrower?.entityName ??
					borrower?.name ??
					[borrower?.firstName, borrower?.lastName].filter(Boolean).join(" ")
			)
			.filter(Boolean)
			.join(" / ");
	}

	return row?.borrowerName ?? row?.borrower ?? "-";
};

const getConditionStatusTone = (status) => {
	const normalized = String(status ?? "").toLowerCase();
	if (
		normalized.includes("fulfill") ||
		normalized.includes("received") ||
		normalized.includes("matched")
	) {
		return "success";
	}

	if (normalized.includes("open") || normalized.includes("pending")) {
		return "danger";
	}

	return "neutral";
};

const StatusPill = ({ text, tone = "neutral" }) => {
	const className = `${styles.statusPill} ${
		styles[`statusPill${tone.charAt(0).toUpperCase()}${tone.slice(1)}`]
	}`;

	return (
		<Box className={className}>
			<Body2
				text={text}
				extraClass={styles.statusPillText}
				fontType="semibold"
			/>
		</Box>
	);
};

const DetailRow = ({ label, value, valueClassName }) => (
	<Stack direction="row" className={styles.detailRow}>
		<Body2 text={label} extraClass={styles.detailLabel} />
		<Body2 text={value} extraClass={valueClassName || styles.detailValue} />
	</Stack>
);

const getConditionIdentifier = (condition) =>
	condition?.entityId ??
	condition?.id ??
	condition?.conditionId ??
	condition?.condition_id ??
	condition?.value ??
	condition?.label ??
	"";

const getDocumentIdentifier = (document) =>
	document?.entityId ??
	document?.attachmentId ??
	document?.id ??
	document?.documentId ??
	document?.assignedTo?.entityId ??
	"";

const getAssignedToIdentifier = (assignedTo) => {
	if (Array.isArray(assignedTo)) {
		for (const item of assignedTo) {
			const identifier =
				item?.entityId ??
				item?.id ??
				item?.entityID ??
				item?.documentId ??
				item?.attachmentId ??
				item?.conditionId ??
				"";

			if (String(identifier).trim()) {
				return String(identifier).trim();
			}
		}

		return "";
	}

	if (typeof assignedTo === "string") {
		return assignedTo.trim();
	}

	return String(
		assignedTo?.entityId ??
			assignedTo?.id ??
			assignedTo?.entityID ??
			assignedTo?.documentId ??
			assignedTo?.attachmentId ??
			assignedTo?.conditionId ??
			""
	).trim();
};

const DocumentTrackerDetailDrawer = ({
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	handleOpenCommonErrorModal,
	isConditionStatusLoaded = false,
	conditionTypeOptions: conditionTypeOptionsProp = [],
	drawerData,
}) => {
	const row = drawerData?.selectedDocument ?? {};
	const params = useParams();
	const loanGuid = drawerData?.loanId;
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);
	const [attachEnhancedConditionDocument] =
		useAttachEnhancedConditionDocumentMutation();
	const [attachDocumentCondition] = useAttachDocumentConditionMutation();
	const [assignDocumentAttachment] = useAssignDocumentAttachmentMutation();
	const [updateFieldValuesTpo] = useUpdateFieldValuesTpoMutation();
	const [enhancedConditionAttachmentUploadUrl] =
		useEnhancedConditionAttachmentUploadUrlMutation();
	const [attachEnhancedConditionDocumentToUploadUrl] =
		useAttachEnhancedConditionDocumentToUploadUrlMutation();
	const [enhancedConditionUploadFileForS3Fallback] =
		useEnhancedConditionUploadFileForS3FallbackMutation();
	const [standardDocumentUploadFileForS3Fallback] =
		useStandardDocumentUploadFileForS3FallbackMutation();
	const uploadSuccessModalShownRef = useRef(false);
	const [enhancedConditionRemoveAttachment] =
		useEnhancedConditionRemoveAttachmentMutation();
	const [enhancedConditionRemoveCondition] =
		useEnhancedConditionRemoveConditionMutation();
	const [standardConditionRemoveCondition] =
		useStandardConditionRemoveConditionMutation();
	const [getLoanDocument] = useGetLoanDocumentMutation();
	const [getUserEmailByDocument] = useGetUserEmailByDocumentMutation();
	const [enhancedConditionsTemplates] =
		useEnhancedConditionsTemplatesMutation();
	const [enhancedConditionsTemplateDetails] =
		useEnhancedConditionsTemplateDetailsMutation();
	const [enhancedConditionsEFolderCreation] =
		useEnhancedConditionsEFolderCreationMutation();
	const [enhancedConditionsDocumentsList] =
		useEnhancedConditionsDocumentsListMutation();
	const [getEnhancedConditionsEfolderDocumentsList] =
		useEnhancedConditionsEfolderDocumentsListMutation();
	const [getEnhancedConditionsEfolderDocumentsDownloadUrlsList] =
		useEnhancedConditionsEfolderDocumentsDownloadUrlsListMutation();
	const [downloadFileFromDownloadUrl] =
		useEnhancedConditionDownloadFileFromDownloadUrlMutation();

	const matchingConditionDocuments = useMemo(
		() =>
			drawerData?.matchingConditionDocuments ?? drawerData?.conditions ?? [],
		[drawerData?.matchingConditionDocuments, drawerData?.conditions]
	);
	const {
		hasMatchingAttachment,
		isIndexed,
		isXpanseProcessingRecord,
		matchingConditionCount,
	} = useMemo(
		() => getTrackerMatchingConditionInfo(row, matchingConditionDocuments),
		[row, matchingConditionDocuments]
	);

	const [tabValue, setTabValue] = useState(0);
	const [uploadedBy, setUploadedBy] = useState("");
	const [isFetchingUploadedBy, setIsFetchingUploadedBy] = useState(false);
	const [encompassFolderDetailsData, setEncompassFolderDetailsData] =
		useState(null);
	const [
		isFetchingEncompassFolderDetails,
		setIsFetchingEncompassFolderDetails,
	] = useState(false);

	const [selectedConditionTypes, setSelectedConditionTypes] = useState([]);
	const [unlinkedConditionIds, setUnlinkedConditionIds] = useState([]);
	const currentOrganization = selectedOrganization || organizationName;
	const documentId = String(getDocumentIdentifier(row) ?? "").trim();
	const uploadedByDocumentId = getDocumentTrackerUploadedByDocumentId(row);
	const assignedToIdentifier = useMemo(
		() => getAssignedToIdentifier(row?.assignedTo),
		[row?.assignedTo]
	);
	const uploadedByFallbackText = getDocumentTrackerUploadedByFallbackText(row);
	const encompassAttachments = useMemo(
		() =>
			Array.isArray(encompassFolderDetailsData?.attachments)
				? [...encompassFolderDetailsData.attachments]
				: [],
		[encompassFolderDetailsData?.attachments]
	);
	const attachmentSizeInfo = useMemo(() => {
		const selectedAttachmentIds = new Set(
			[
				row?.entityId,
				row?.attachmentId,
				row?.id,
				row?.documentId,
				row?.document_id,
			]
				.map((value) => String(value ?? "").trim())
				.filter(Boolean)
		);

		const matchedAttachment = encompassAttachments.find((attachment) =>
			selectedAttachmentIds.has(getAttachmentIdentifier(attachment))
		);

		const documentSizeBytes = getAttachmentSizeBytes(matchedAttachment ?? row);
		const totalDocumentSizeBytes = encompassAttachments.reduce(
			(total, attachment) => total + getAttachmentSizeBytes(attachment),
			0
		);

		return {
			documentSize: formatBytesWithUnit(documentSizeBytes),
			totalDocumentSize: formatBytesWithUnit(totalDocumentSizeBytes),
		};
	}, [
		encompassAttachments,
		row?.attachmentId,
		row?.documentId,
		row?.document_id,
		row?.entityId,
		row?.id,
		row,
	]);
	const encompassFolderDetails = useMemo(
		() => ({
			...row,
			...(encompassFolderDetailsData ?? {}),
			title: String(encompassFolderDetailsData?.title ?? "").trim() || "-",
			status:
				String(encompassFolderDetailsData?.documentStatus ?? "").trim() || "-",
			documentSize: attachmentSizeInfo.documentSize,
			totalDocumentSize: attachmentSizeInfo.totalDocumentSize,
		}),
		[row, encompassFolderDetailsData, attachmentSizeInfo]
	);
	const hasEncompassFolderDetails = Boolean(encompassFolderDetailsData);

	const handleErrorModal = (errorKey, data) => {
		handleCloseProcessingModal?.();

		if (
			data?.status === "FETCH_ERROR" ||
			data?.status === 413 ||
			data?.OriginalStatus === 413 ||
			data?.originalStatus === 413
		) {
			handleOpenCommonErrorModal?.(errorKey ?? "FILE_SIZE_TOO_LARGE");
		} else {
			handleOpenCommonErrorModal?.(errorKey ?? "DOCUMENT_UPLOAD_ERROR");
		}
	};

	useEffect(() => {
		let isMounted = true;

		if (row?.__isPendingRetryRecord) {
			setIsFetchingUploadedBy(false);
			setUploadedBy(row?.uploadedBy ?? "");
			return () => {
				isMounted = false;
			};
		}

		if (!shouldResolveDocumentTrackerUploadedBy(row)) {
			setIsFetchingUploadedBy(false);
			setUploadedBy(uploadedByFallbackText);
			return () => {
				isMounted = false;
			};
		}

		if (!uploadedByDocumentId) {
			setIsFetchingUploadedBy(false);
			setUploadedBy(uploadedByFallbackText);
			return () => {
				isMounted = false;
			};
		}

		const cachedEmail =
			documentTrackerUploadedByCache.get(uploadedByDocumentId);
		if (cachedEmail !== undefined) {
			setIsFetchingUploadedBy(false);
			setUploadedBy(cachedEmail || uploadedByFallbackText);
			return () => {
				isMounted = false;
			};
		}

		const loadDocumentEmail = async () => {
			setIsFetchingUploadedBy(true);

			try {
				let requestPromise =
					documentTrackerUploadedByRequestCache.get(uploadedByDocumentId);

				if (!requestPromise) {
					requestPromise = getUserEmailByDocument({
						documentId: uploadedByDocumentId,
					})
						.unwrap()
						.then((response) => {
							const email = getDocumentTrackerUploadedByEmailFromResponse(
								response,
								uploadedByDocumentId
							);

							documentTrackerUploadedByCache.set(uploadedByDocumentId, email);
							return email;
						})
						.catch((error) => {
							console.warn(
								`[Document Email] lookup failed for ${uploadedByDocumentId}:`,
								error
							);
							documentTrackerUploadedByCache.set(uploadedByDocumentId, "");
							return "";
						})
						.finally(() => {
							documentTrackerUploadedByRequestCache.delete(
								uploadedByDocumentId
							);
						});

					documentTrackerUploadedByRequestCache.set(
						uploadedByDocumentId,
						requestPromise
					);
				}

				const resolvedEmail = await requestPromise;

				if (isMounted) {
					setUploadedBy(resolvedEmail || uploadedByFallbackText);
				}
			} catch (error) {
				if (isMounted) {
					setUploadedBy(uploadedByFallbackText);
				}
			} finally {
				if (isMounted) {
					setIsFetchingUploadedBy(false);
				}
			}
		};

		loadDocumentEmail();

		return () => {
			isMounted = false;
		};
	}, [
		uploadedByDocumentId,
		getUserEmailByDocument,
		row?.__isPendingRetryRecord,
		row?.uploadedBy,
		row?.createdBy?.entityId,
		row?.createdBy?.name,
	]);
	useEffect(() => {
		let isMounted = true;

		if (!assignedToIdentifier || !loanGuid) {
			setIsFetchingEncompassFolderDetails(false);
			setEncompassFolderDetailsData(null);
			return () => {
				isMounted = false;
			};
		}

		const cacheKey = `${loanGuid}:${assignedToIdentifier}`;
		const cachedDetails = loanDocumentDetailsCache.get(cacheKey);
		if (cachedDetails !== undefined) {
			setIsFetchingEncompassFolderDetails(false);
			setEncompassFolderDetailsData(cachedDetails);
			return () => {
				isMounted = false;
			};
		}

		const loadEncompassFolderDetails = async () => {
			setIsFetchingEncompassFolderDetails(true);

			try {
				let requestPromise = loanDocumentDetailsRequestCache.get(cacheKey);

				if (!requestPromise) {
					requestPromise = getLoanDocument({
						loanGuid,
						documentId: assignedToIdentifier,
					})
						.unwrap()
						.then((response) => {
							const payload =
								response?.response ?? response?.data ?? response ?? {};
							const details = payload;

							loanDocumentDetailsCache.set(cacheKey, details);
							return details;
						})
						.catch((error) => {
							console.warn(
								`[Loan Document] lookup failed for ${cacheKey}:`,
								error
							);
							return null;
						})
						.finally(() => {
							loanDocumentDetailsRequestCache.delete(cacheKey);
						});

					loanDocumentDetailsRequestCache.set(cacheKey, requestPromise);
				}

				const resolvedDetails = await requestPromise;

				if (isMounted) {
					setEncompassFolderDetailsData(resolvedDetails);
				}
			} catch (error) {
				if (isMounted) {
					setEncompassFolderDetailsData(null);
					handleErrorModal("DOCUMENT_FETCH_ERROR", error);
				}
			} finally {
				if (isMounted) {
					setIsFetchingEncompassFolderDetails(false);
				}
			}
		};

		loadEncompassFolderDetails();

		return () => {
			isMounted = false;
		};
	}, [assignedToIdentifier, getLoanDocument, handleErrorModal, loanGuid]);

	const normalizeConditionOption = (option) => {
		if (typeof option === "string") {
			return {
				id: option,
				title: option,
				label: option,
				name: option,
				entityName: option,
				category: "",
				internalDescription: "",
			};
		}

		const title =
			option?.title ??
			option?.label ??
			option?.name ??
			option?.entityName ??
			"";
		const id = option?.id ?? option?.entityId ?? option?.value ?? title ?? "";

		return {
			...option,
			id,
			title,
			conditionType: option?.conditionType ?? title,
			label: title,
			name: title,
			entityName: title,
			category: option?.category ?? option?.conditionCategory ?? "",
			description:
				option?.description ??
				option?.internalDescription ??
				option?.externalDescription ??
				"",
			internalDescription:
				option?.internalDescription ?? option?.description ?? "",
		};
	};

	const conditionTypeOptions =
		Array.isArray(conditionTypeOptionsProp) &&
		conditionTypeOptionsProp.length > 0
			? conditionTypeOptionsProp
			: (drawerData?.conditionTypeOptions ?? []);

	const selectedConditionValues = useMemo(
		() => (Array.isArray(selectedConditionTypes) ? selectedConditionTypes : []),
		[selectedConditionTypes]
	);
	const conditionRows = useMemo(
		() =>
			conditionTypeOptions
				.map(normalizeConditionOption)
				.filter(
					(row) =>
						!["cleared", "fulfilled", "waived"].includes(
							row.sub_status?.trim().toLowerCase()
						)
				),
		[conditionTypeOptions]
	);

	const isLoadingConditionsList =
		!isConditionStatusLoaded && conditionRows.length === 0;

	const selectedConditionValuesWithLabels = useMemo(
		() =>
			selectedConditionValues.map((conditionId) => {
				const matchedCondition = conditionRows.find(
					(row) =>
						String(row?.id ?? row?.entityId ?? row?.value ?? "") ===
						String(conditionId)
				);

				return {
					id: conditionId,
					label:
						matchedCondition?.label ||
						matchedCondition?.title ||
						matchedCondition?.name ||
						conditionId,
					conditionType:
						matchedCondition?.conditionType ??
						matchedCondition?.title ??
						matchedCondition?.label ??
						matchedCondition?.name ??
						"",
					description:
						matchedCondition?.description ||
						matchedCondition?.internalDescription ||
						matchedCondition?.externalDescription ||
						"",
				};
			}),
		[selectedConditionValues, conditionRows]
	);

	const conditionRowIdMap = useMemo(
		() =>
			new Map(
				conditionRows.map((row) => [
					String(row?.id ?? row?.entityId ?? row?.value ?? ""),
					row?.id ?? row?.entityId ?? row?.value ?? "",
				])
			),
		[conditionRows]
	);

	const normalizeSelectedConditionTypes = (value) =>
		(Array.isArray(value) ? value : [value])
			.map((item) =>
				typeof item === "string"
					? item
					: (item?.id ?? item?.entityId ?? item?.value ?? item?.label ?? "")
			)
			.filter(Boolean);

	const getConditionRowSelectionId = useCallback(
		(condition) => {
			const rawConditionId = getConditionIdentifier(condition);

			return conditionRowIdMap.get(String(rawConditionId)) ?? rawConditionId;
		},
		[conditionRowIdMap]
	);

	const explicitSelectedConditionValues = useMemo(
		() =>
			normalizeSelectedConditionTypes(
				drawerData?.selectedConditionTypes ?? drawerData?.selectedConditionType
			),
		[drawerData?.selectedConditionTypes, drawerData?.selectedConditionType]
	);

	const attachEnhancedConditionDocumentRecord = async ({
		documentId,
		conditionId,
		conditionName,
	}) => {
		try {
			const attachDocumentResponse = await attachEnhancedConditionDocument({
				loanGuid,
				data: [
					{
						entityId: documentId,
						entityType: "Document",
						conditionName,
					},
				],
				conditionId,
			}).unwrap();

			const responseItems = attachDocumentResponse?.data ?? [];

			if (responseItems.some(isRetryItem)) {
				handleCloseProcessingModal?.();
				handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
				throw createRetryHandledError();
			}

			if (
				responseItems.length === 0 ||
				responseItems.some((item) => item?.status === "failure")
			) {
				handleErrorModal(
					"ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED",
					attachDocumentResponse
				);
				return false;
			}

			return true;
		} catch (error) {
			if (isRetryHandledError(error)) {
				throw error;
			}
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED", error);
			return false;
		}
	};

	const getSelectedFile = (item) =>
		item?.blob ?? item?.file ?? item?.raw?.file ?? item;

	const buildUploadFilesFromSelectedDocument = async (selectedDocument) => {
		const attachmentIds = [getDocumentIdentifier(selectedDocument)]
			.filter((value) => value !== undefined && value !== null)
			.map(String);
		if (attachmentIds.length === 0) {
			throw new Error("Selected document attachment id not found");
		}

		const downloadUrlsResponse =
			await getEnhancedConditionsEfolderDocumentsDownloadUrlsList({
				loanGuid,
				attachmentIds,
			}).unwrap();
		const attachment = downloadUrlsResponse?.data?.attachments?.find(
			(item) =>
				attachmentIds.includes(String(item?.id)) ||
				attachmentIds.includes(String(item?.attachmentId)) ||
				attachmentIds.includes(String(item?.entityId))
		);

		const downloadUrl = attachment?.originalUrls?.[0] ?? attachment?.url;

		if (!downloadUrl) {
			throw new Error("Document download url unavailable");
		}

		const downloadedFile = await downloadFileFromDownloadUrl({
			downloadUrl,
			authorization: attachment?.authorizationHeader,
		}).unwrap();

		const fileName =
			attachment?.title ??
			selectedDocument?.title ??
			selectedDocument?.name ??
			GENERIC_FALLBACK_FILE_NAME;
		const contentType =
			attachment?.contentType ||
			selectedDocument?.contentType ||
			downloadedFile?.type ||
			getMimeTypeFromFilename(fileName) ||
			GENERIC_FALLBACK_CONTENT_TYPE;
		const fileBlob =
			downloadedFile instanceof Blob
				? downloadedFile
				: new Blob([downloadedFile], { type: contentType });

		if (typeof File !== "undefined") {
			const file = new File([fileBlob], fileName, {
				type: fileBlob.type || contentType,
			});

			return Object.assign(file, {
				sourceDocument: selectedDocument,
				sourceDocumentId: selectedDocument?.id,
				sourceDocumentFileSize: fileBlob?.size ?? file.size ?? 0,
			});
		}

		return {
			name: fileName,
			type: fileBlob.type || contentType,
			size: fileBlob?.size ?? 0,
			sourceDocument: selectedDocument,
			sourceDocumentId: selectedDocument?.id,
			sourceDocumentFileSize: fileBlob?.size ?? 0,
			blob: fileBlob,
		};
	};

	const buildEnhancedUploadBody = (
		fileDetails,
		efolderId,
		selectedDocument
	) => {
		const resolvedFile = getSelectedFile(fileDetails);
		const resolvedDocument =
			selectedDocument ?? fileDetails?.sourceDocument ?? {};
		const fileName =
			resolvedFile?.name ??
			resolvedDocument?.title ??
			resolvedDocument?.name ??
			GENERIC_FALLBACK_FILE_NAME;

		return {
			file: {
				contentType:
					resolvedFile?.type ||
					getMimeTypeFromFilename(fileName) ||
					GENERIC_FALLBACK_CONTENT_TYPE,
				name: fileName,
				size: resolvedFile?.size ?? 0,
			},
			title: fileName,
			assignTo: {
				entityId: efolderId,
				entityType: "Document",
			},
		};
	};

	const getEnhancedUploadRequest = async (enhancedUploadBody) => {
		try {
			const uploadDocumentResponse = await enhancedConditionAttachmentUploadUrl(
				{
					loanGuid,
					file: enhancedUploadBody?.file,
					title: enhancedUploadBody?.title,
					assignTo: enhancedUploadBody?.assignTo,
					user_email: currentLoggedInUserEmail,
				}
			).unwrap();

			const uploadData = uploadDocumentResponse?.data ?? {};

			if (uploadData?.needsS3Fallback) {
				return {
					needsS3Fallback: true,
					conditionId: uploadData?.conditionId,
					conditionName: uploadData?.conditionName,
					entityId: uploadData?.entityId,
					entityType: uploadData?.entityType,
					title: uploadData?.title,
					attachmentId: uploadData?.attachmentId,
				};
			}

			const uploadUrl = uploadData?.multiChunkRequired
				? uploadData?.multiChunk?.commitUrl
				: uploadData?.uploadUrl || uploadData?.url;
			const uploadAuthorization =
				uploadData?.authorizationHeader ||
				uploadData?.authorization ||
				uploadData?.authHeader ||
				uploadData?.auth ||
				uploadData?.signature;

			if (!uploadUrl) {
				handleErrorModal(
					"ENHANCED_CONDITION_UPLOAD_FAILED",
					uploadDocumentResponse
				);
				return null;
			}

			return {
				uploadUrl,
				uploadAuthorization,
				multiChunkRequired: uploadData?.multiChunkRequired,
				multiChunk: uploadData?.multiChunk,
				attachmentId: uploadData?.attachmentId,
				needsS3Fallback: false,
			};
		} catch (error) {
			if (isConflictError(error)) {
				throw error;
			}
			handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", error);
			return null;
		}
	};

	const uploadEnhancedConditionFileToS3Fallback = async (
		file,
		{ conditionId, conditionName, entityId, entityType, title }
	) => {
		try {
			const selectedFile = getSelectedFile(file);

			const formData = new FormData();
			formData.append("title", title || selectedFile?.name || "");
			formData.append("condition_id", conditionId ?? "");
			formData.append("condition_name", conditionName ?? "");
			formData.append("entity_id", entityId ?? "");
			formData.append("entity_type", entityType || "Document");
			formData.append("file", selectedFile, selectedFile?.name);

			const fallbackResponse = await enhancedConditionUploadFileForS3Fallback({
				loanGuid,
				formData,
			}).unwrap();

			return fallbackResponse;
		} catch (error) {
			if (isConflictError(error)) {
				throw error;
			}
			console.error("document-tracker detail: S3 fallback upload error", error);
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
			return null;
		}
	};

	const uploadEnhancedConditionFile = async ({
		uploadUrl,
		uploadAuthorization,
		multiChunkRequired,
		multiChunk,
		file,
		conditionId,
		conditionName,
		entityId,
		entityType,
		title,
	}) => {
		try {
			const selectedFile = getSelectedFile(file);

			if (multiChunkRequired || Array.isArray(multiChunk?.chunkList)) {
				const multipartResponse = await uploadMultipartFile({
					file: selectedFile,
					chunkList: multiChunk?.chunkList,
					commitUrl: uploadUrl,
					authorization: uploadAuthorization,
				});

				return multipartResponse;
			}

			const uploadResponse = await attachEnhancedConditionDocumentToUploadUrl({
				uploadUrl,
				authorization: uploadAuthorization,
				file: selectedFile,
				user_email: currentLoggedInUserEmail,
				loanGuid,
				conditionId,
				conditionName,
				entityId,
				entityType,
				title,
			}).unwrap();

			if (isRetryResponse(uploadResponse)) {
				handleCloseProcessingModal?.();
				handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
				throw createRetryHandledError();
			}

			return uploadResponse;
		} catch (error) {
			if (isRetryHandledError(error)) {
				throw error;
			}
			if (isConflictError(error)) {
				throw error;
			}
			console.error("document-tracker detail: upload error", error);
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
			return null;
		}
	};

	const normalizeFolderName = (value) =>
		String(value ?? "")
			.trim()
			.toLowerCase();

	const getDocumentFolderName = (document) =>
		normalizeFolderName(
			document?.title ?? document?.document_name ?? document?.name
		);

	const checkIfFolderExists = async (selectedLoanId, selectedDocument) => {
		try {
			const loanDocuments = await getEnhancedConditionsDocumentsList({
				loanGuid: selectedLoanId,
				apiRequest: enhancedConditionsDocumentsList,
				handleErrorModal,
				errorKey: "ENHANCED_CONDITION_LOAN_DOCUMENTS_UNAVAILABLE",
			});

			if (!loanDocuments) {
				return null;
			}

			const selectedFolderName = normalizeFolderName(selectedDocument?.title);

			const matchedFolder = loanDocuments?.data?.find((doc) => {
				const existingFolderName = getDocumentFolderName(doc);

				return (
					existingFolderName === selectedFolderName ||
					existingFolderName.includes(selectedFolderName) ||
					selectedFolderName.includes(existingFolderName)
				);
			});

			if (matchedFolder) {
				return matchedFolder?.id;
			}

			const createdFolder = await enhancedConditionsEFolderCreation({
				documentName: selectedDocument?.title,
				description:
					selectedDocument?.description ||
					selectedDocument?.internalDescription ||
					selectedDocument?.externalDescription,
				loanGuid: selectedLoanId,
			}).unwrap();

			return createdFolder?.data?.[0]?.id ?? null;
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_EFOLDER_CREATION_FAILED", error);
			return null;
		}
	};

	const getSelectedDocumentHasConditions = (selectedDocument) =>
		Boolean(selectedDocument?.hasConditions) ||
		(Array.isArray(selectedDocument?.conditions)
			? selectedDocument.conditions.length > 0
			: false);

	const resolveHasConditionsForSelectedConditions = async ({
		selectedDocument,
		documentId,
		selectedConditionsToProcess = [],
	}) => {
		const fallbackHasConditions =
			getSelectedDocumentHasConditions(selectedDocument);

		if (selectedConditionsToProcess.length <= 1) {
			return fallbackHasConditions;
		}

		const selectedConditionIds = new Set(
			selectedConditionsToProcess
				.map((condition) => String(getConditionIdentifier(condition)).trim())
				.filter(Boolean)
		);

		if (selectedConditionIds.size === 0) {
			return fallbackHasConditions;
		}

		const loanDocuments = await getEnhancedConditionsDocumentsList({
			loanGuid,
			apiRequest: enhancedConditionsDocumentsList,
			handleErrorModal,
			errorKey: "ENHANCED_CONDITION_LOAN_DOCUMENTS_UNAVAILABLE",
		});

		if (!loanDocuments) {
			return null;
		}

		const selectedDocumentId = String(
			documentId ?? getDocumentIdentifier(selectedDocument) ?? ""
		).trim();
		const selectedDocumentTitle = String(
			selectedDocument?.title ??
				selectedDocument?.name ??
				selectedDocument?.document_name ??
				""
		)
			.trim()
			.toLowerCase();

		const refreshedSelectedDocument = Array.isArray(loanDocuments?.data)
			? loanDocuments.data.find((document) => {
					const documentIdentifiers = [
						getDocumentIdentifier(document),
						document?.entityId,
						document?.id,
						document?.attachmentId,
						document?.documentId,
					]
						.map((value) => String(value ?? "").trim())
						.filter(Boolean);
					const documentTitle = String(
						document?.title ?? document?.name ?? document?.document_name ?? ""
					)
						.trim()
						.toLowerCase();

					return (
						(selectedDocumentId &&
							documentIdentifiers.includes(selectedDocumentId)) ||
						(selectedDocumentTitle &&
							documentTitle &&
							documentTitle === selectedDocumentTitle)
					);
				})
			: null;

		if (!refreshedSelectedDocument) {
			return true;
		}

		const refreshedConditions = Array.isArray(
			refreshedSelectedDocument?.conditions
		)
			? refreshedSelectedDocument.conditions
			: [];
		const hasMatchingSelectedCondition = refreshedConditions.some((condition) =>
			selectedConditionIds.has(String(getConditionIdentifier(condition)).trim())
		);
		const hasAnyRefreshedCondition =
			Boolean(refreshedSelectedDocument?.hasConditions) ||
			refreshedConditions.length > 0;

		return hasMatchingSelectedCondition || hasAnyRefreshedCondition;
	};

	const resolveShouldUseDirectAttachmentForSelectedConditions = async ({
		selectedDocument,
		documentId,
		selectedConditionsToProcess = [],
		hasAssignedTo = false,
		useFreshDocumentLookup = false,
	}) => {
		const resolvedHasConditions = useFreshDocumentLookup
			? await resolveHasConditionsForSelectedConditions({
					selectedDocument,
					documentId,
					selectedConditionsToProcess,
				})
			: getSelectedDocumentHasConditions(selectedDocument);

		if (resolvedHasConditions === null) {
			return null;
		}

		return !hasAssignedTo && !resolvedHasConditions;
	};

	const efolderDetails = async (selectedDocument) => {
		try {
			let documentName = selectedDocument?.label;

			if (isEnhancedEnabled) {
				const templatesResponse = await enhancedConditionsTemplates().unwrap();

				const matchedTemplate = templatesResponse?.data?.find(
					(item) =>
						documentName &&
						item?.title &&
						documentName.toLowerCase().includes(item.title.toLowerCase())
				);

				const selectedId = matchedTemplate?.id;

				if (!selectedId) {
					handleErrorModal(
						"ENHANCED_CONDITION_UPLOAD_FAILED",
						templatesResponse
					);
					return null;
				}

				const templateDetails = await enhancedConditionsTemplateDetails({
					id: selectedId,
				}).unwrap();

				const folderNameCondition =
					templateDetails?.data?.connectSettings?.documentOption;

				if (folderNameCondition === "MatchConditionName") {
					return checkIfFolderExists(loanGuid, selectedDocument);
				}

				const efolderDocuments =
					await getEnhancedConditionsEfolderDocumentsList().unwrap();
				documentName = efolderDocuments?.data?.find(
					(doc) =>
						doc?.id ===
						templateDetails?.data?.connectSettings?.documentTemplate?.entityId
				)?.name;
			}

			return checkIfFolderExists(loanGuid, {
				title: documentName,
				internalDescription: `Default Document with name ${documentName}`,
			});
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_UPLOAD_FAILED", error);
			return null;
		}
	};

	useEffect(() => {}, [selectedConditionTypes]);

	useEffect(() => {
		setUnlinkedConditionIds([]);
	}, [drawerData?.selectedDocument?.id]);

	const enhancedConditionsStatusFromStore = useSelector(
		selectEnhancedConditionStatus
	);
	const enhancedConditionsStatus = String(
		enhancedConditionsStatusFromStore ?? ""
	)
		.trim()
		.toUpperCase();
	const isEnhancedEnabled = enhancedConditionsStatus === "Y";
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const currentLoggedInUserEmail = String(
		impersonatedUser?.email ?? userDetails?.email ?? ""
	).trim();

	const handleUnlinkCondition = async (condition, matchedConditionType) => {
		if (isEnhancedEnabled) {
			const conditionId = getConditionIdentifier(condition);
			if (!conditionId) return;

			handleOpenProcessingModal?.();
			setProcessingText?.("Unlinking selected condition");

			try {
				const unlinkResponse = await enhancedConditionRemoveCondition({
					loanGuid,
					data: [
						{
							id: conditionId,
						},
					],
				}).unwrap();

				if (unlinkResponse?.status && unlinkResponse.status !== "success") {
					throw unlinkResponse;
				}

				setUnlinkedConditionIds((prev) => {
					if (!conditionId || prev.includes(conditionId)) {
						return prev;
					}

					return [...prev, conditionId];
				});

				const selectedConditionRowId = getConditionRowSelectionId(condition);

				setSelectedConditionTypes((prev) =>
					prev.filter((id) => String(id) !== String(selectedConditionRowId))
				);

				await drawerData?.onRefreshDocumentsList?.();
			} catch (error) {
				handleErrorModal("ENHANCED_CONDITION_REMOVE_CONDITION_FAILED", error);
			} finally {
				handleCloseProcessingModal?.();
			}
		} else {
			const conditionId = getConditionIdentifier(condition);
			if (!conditionId) return;

			handleOpenProcessingModal?.();
			setProcessingText?.("Unlinking selected condition");

			try {
				const normalizedMatchedConditionType = String(
					matchedConditionType ?? ""
				)
					.trim()
					.toLowerCase();

				const unlinkResponse = await standardConditionRemoveCondition({
					loanGuid,
					conditionId,
					conditionType: normalizedMatchedConditionType,
				}).unwrap();

				if (unlinkResponse?.status && unlinkResponse.status !== "success") {
					throw unlinkResponse;
				}

				setUnlinkedConditionIds((prev) => {
					if (!conditionId || prev.includes(conditionId)) {
						return prev;
					}

					return [...prev, conditionId];
				});

				const selectedConditionRowId = getConditionRowSelectionId(condition);

				setSelectedConditionTypes((prev) =>
					prev.filter((id) => String(id) !== String(selectedConditionRowId))
				);

				await drawerData?.onRefreshDocumentsList?.();
			} catch (error) {
				handleErrorModal("ENHANCED_CONDITION_REMOVE_CONDITION_FAILED", error);
			} finally {
				handleCloseProcessingModal?.();
			}
		}
	};

	const documentName =
		row?.documentName ?? row?.title ?? row?.entityName ?? row?.name ?? "-";
	const attachmentId = row?.attachmentId ?? row?.entityId ?? row?.id ?? "-";
	const uploadedAt = formatDateTime(row?.updatedDate ?? row?.createdDate);
	const fileSize = formatBytesWithUnit(
		row?.fileSize ?? row?.size ?? row?.documentSize
	);
	const hasAssignedTo = Array.isArray(row?.assignedTo)
		? row.assignedTo.length > 0
		: Boolean(row?.assignedTo);
	const {
		isIndexedDocument,
		isPendingUpdateToLoanFile,
		loganStatusText,
		pipelineDotColors,
		pipelineStatusText,
		isYellowLoganStatus,
	} = getDocumentPipelineStatusPresentation({
		row,
		isIndexed,
		isXpanseProcessingRecord,
		matchingConditionCount,
		hasMatchingAttachment,
		hasAssignedTo,
	});
	const statusLabel =
		row?.statusLabel ?? row?.pipelineStatusText ?? pipelineStatusText;
	const eFolderStatus =
		row?.eFolderStatus ?? row?.folderStatus ?? row?.indexStatus ?? "Indexed";
	const borrowerPair = getBorrowerPair(row);
	const hideAttachmentId = Boolean(row?.__isPendingRetryRecord);
	const attachmentIdDisplayValue = hideAttachmentId ? "-" : attachmentId;
	const conditionType =
		drawerData?.selectedConditionType ??
		row?.conditionType ??
		row?.condition_type ??
		"-";

	const visibleMatchingConditionDocuments = useMemo(
		() =>
			matchingConditionDocuments
				.map((item) => ({
					...item,
					conditions: (item?.conditions ?? []).filter((condition) => {
						const conditionId = getConditionIdentifier(condition);
						return !unlinkedConditionIds.includes(conditionId);
					}),
				}))
				.filter((item) => (item?.conditions ?? []).length > 0),
		[matchingConditionDocuments, unlinkedConditionIds]
	);
	const visibleConditionCount = useMemo(
		() =>
			visibleMatchingConditionDocuments.reduce(
				(total, item) => total + (item?.conditions?.length ?? 0),
				0
			),
		[visibleMatchingConditionDocuments]
	);
	const autoSelectedConditionValues = useMemo(
		() =>
			visibleMatchingConditionDocuments
				.flatMap((item) =>
					(item?.conditions ?? []).map((condition) =>
						getConditionRowSelectionId(condition)
					)
				)
				.filter(Boolean),
		[visibleMatchingConditionDocuments, getConditionRowSelectionId]
	);

	const defaultSelectedConditionValues = useMemo(
		() =>
			autoSelectedConditionValues.length > 0
				? Array.from(new Set(autoSelectedConditionValues))
				: explicitSelectedConditionValues,
		[autoSelectedConditionValues, explicitSelectedConditionValues]
	);

	const conditionIdsToAttach = useMemo(
		() =>
			selectedConditionValues.filter(
				(conditionId) => !autoSelectedConditionValues.includes(conditionId)
			),
		[selectedConditionValues, autoSelectedConditionValues]
	);

	const defaultSelectedConditionValuesRef = useRef(
		defaultSelectedConditionValues
	);

	useEffect(() => {
		defaultSelectedConditionValuesRef.current = defaultSelectedConditionValues;
	}, [defaultSelectedConditionValues]);

	useEffect(() => {
		setSelectedConditionTypes(defaultSelectedConditionValuesRef.current);
	}, [drawerData?.selectedDocument?.id]);

	const openUploadSuccessModalOnce = (text) => {
		if (uploadSuccessModalShownRef.current) {
			return;
		}

		uploadSuccessModalShownRef.current = true;
		handleCloseProcessingModal?.();
		setSuccessText?.(text);
		handleOpenSuccessModal?.();
	};

	const retryConflictWithS3Fallback = async ({
		conditionId,
		conditionName,
		conditionType,
		entityId,
		entityType = "Document",
		title,
		file,
	} = {}) => {
		const selectedFile = getSelectedFile(file);

		if (!selectedFile) {
			handleErrorModal(
				"ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED",
				new Error("Missing file for S3 fallback retry")
			);
			return null;
		}

		const S3_FALLBACK_CHUNK_SIZE_BYTES = 59 * 1024 * 1024;

		try {
			return await uploadFileWithChunking({
				file: selectedFile,
				thresholdBytes: S3_FALLBACK_CHUNK_SIZE_BYTES,
				chunkSizeBytes: S3_FALLBACK_CHUNK_SIZE_BYTES,
				uploadPart: async ({ file: uploadFile, meta }) => {
					const formData = new FormData();
					formData.append("title", title || selectedFile.name || "");
					formData.append("condition_id", conditionId ?? "");
					formData.append("condition_name", conditionName ?? "");
					formData.append("condition_type", conditionType ?? "");
					formData.append("entity_id", entityId ?? "");
					formData.append("entity_type", entityType);
					formData.append("uploaded_by", currentLoggedInUserEmail);
					formData.append("file", uploadFile, selectedFile.name);
					formData.append("loan_guid", loanGuid);

					if (meta?.isChunked) {
						formData.append("chunk_index", String(meta.chunkIndex));
						formData.append("total_chunks", String(meta.totalChunks));
						formData.append("upload_id", meta.uploadId);
						formData.append("original_file_name", meta.originalFileName);
						formData.append(
							"original_file_size",
							String(meta.originalFileSize)
						);
						formData.append("is_chunked", "true");
						formData.append("is_last_chunk", String(meta.isLastChunk));
					}

					if (isEnhancedEnabled) {
						return enhancedConditionUploadFileForS3Fallback({
							loanGuid,
							formData,
						}).unwrap();
					}

					return standardDocumentUploadFileForS3Fallback({
						loanGuid,
						formData,
					}).unwrap();
				},
			});
		} catch (error) {
			handleErrorModal("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED", error);
			return null;
		}
	};

	const handleSubmitSelectedConditions = async () => {
		if (conditionIdsToAttach.length === 0) {
			return;
		}

		const conditionIdsToAttachSet = new Set(
			conditionIdsToAttach.map((conditionId) => String(conditionId))
		);
		const selectedConditionsToProcess =
			selectedConditionValuesWithLabels.filter((condition) =>
				conditionIdsToAttachSet.has(String(condition?.id))
			);

		if (selectedConditionsToProcess.length === 0) {
			return;
		}

		handleCloseDrawer?.();
		handleOpenProcessingModal?.();
		setProcessingText?.("Linking selected conditions");
		let selectedDocumentFile = null;
		let retryContext = {};

		let valuesArr = [
			{ id: "TPO.X4", value: DateTime.now().toFormat("MM-dd-yyyy") },
			{
				id: "CX.LC.DOC.REC.DATE",
				value: DateTime.fromISO(DateTime.now(), {
					zone: "America/New_York",
				}).toFormat("MM-dd-yyyy hh:mm a"),
			},
		];

		await updateFieldValuesTpo({
			loan_guid: params?.id,
			field_values: valuesArr,
			selectedOrganization: currentOrganization,
			userDetails,
		}).unwrap();

		try {
			const documentId = getDocumentIdentifier(row);
			const selectedDocument = row;
			const hasAssignedTo = Array.isArray(selectedDocument?.assignedTo)
				? selectedDocument.assignedTo.length > 0
				: Boolean(selectedDocument?.assignedTo);
			const multipleSelectedConditions = selectedConditionsToProcess.length > 1;
			if (!documentId) {
				throw new Error("ENHANCED_CONDITION_DOCUMENT_ID_UNAVAILABLE");
			}
			for (const [
				index,
				selectedCondition,
			] of selectedConditionsToProcess.entries()) {
				const shouldUseDirectAttachment =
					await resolveShouldUseDirectAttachmentForSelectedConditions({
						selectedDocument,
						documentId,
						selectedConditionsToProcess,
						hasAssignedTo,
						useFreshDocumentLookup: multipleSelectedConditions && index > 0,
					});

				if (shouldUseDirectAttachment === null) {
					return;
				}

				const conditionId = selectedCondition?.id;
				const conditionName =
					selectedCondition?.label ??
					selectedCondition?.title ??
					selectedCondition?.name ??
					selectedCondition?.id;
				const conditionType = String(
					selectedCondition?.conditionType ??
						selectedCondition?.condition_type ??
						""
				)
					.trim()
					.toLowerCase();

				const efolderId = await efolderDetails(selectedCondition);

				if (!efolderId) {
					throw new Error("ENHANCED_CONDITION_EFOLDER_CREATION_FAILED");
				}

				retryContext = {
					conditionId,
					conditionName,
					conditionType,
					entityId: efolderId,
					entityType: "Document",
					title: selectedDocumentFile?.name ?? selectedDocument?.title,
					file: selectedDocumentFile,
				};

				if (shouldUseDirectAttachment) {
					const attachmentId = String(
						selectedDocument?.id ?? documentId ?? ""
					).trim();

					if (!attachmentId) {
						throw new Error("Selected document attachment id not found");
					}

					const assignDocumentAttachmentResponse =
						await assignDocumentAttachment({
							loanGuid,
							docId: efolderId,
							attachmentId,
						}).unwrap();

					if (
						assignDocumentAttachmentResponse?.response !==
						"Attachment assigned to the document"
					) {
						throw new Error("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED");
					}
				} else {
					if (!selectedDocumentFile) {
						selectedDocumentFile =
							await buildUploadFilesFromSelectedDocument(selectedDocument);
					}

					if (!selectedDocumentFile) {
						throw new Error("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED");
					}

					retryContext = {
						...retryContext,
						file: selectedDocumentFile,
						title: selectedDocumentFile?.name ?? retryContext.title,
					};

					const uploadDetails = await getEnhancedUploadRequest(
						await buildEnhancedUploadBody(
							selectedDocumentFile,
							efolderId,
							selectedDocument
						)
					);

					if (!uploadDetails) {
						throw new Error("ENHANCED_CONDITION_UPLOAD_FAILED");
					}

					const uploadResponse = uploadDetails?.needsS3Fallback
						? await uploadEnhancedConditionFileToS3Fallback(
								selectedDocumentFile,
								{
									conditionId: uploadDetails?.conditionId ?? conditionId,
									conditionName: uploadDetails?.conditionName ?? conditionName,
									entityId: uploadDetails?.entityId ?? efolderId,
									entityType: uploadDetails?.entityType ?? "Document",
									title: uploadDetails?.title ?? selectedDocumentFile?.name,
								}
							)
						: await uploadEnhancedConditionFile({
								uploadUrl: uploadDetails.uploadUrl,
								uploadAuthorization: uploadDetails.uploadAuthorization,
								multiChunkRequired: uploadDetails.multiChunkRequired,
								multiChunk: uploadDetails.multiChunk,
								file: selectedDocumentFile,
								conditionId: uploadDetails?.conditionId ?? conditionId,
								conditionName: uploadDetails?.conditionName ?? conditionName,
								entityId: uploadDetails?.entityId ?? efolderId,
								entityType: uploadDetails?.entityType ?? "Document",
								title: uploadDetails?.title ?? selectedDocumentFile?.name,
							});
					if (!uploadResponse) {
						throw new Error("ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED");
					}
				}

				// const attached = await attachEnhancedConditionDocumentRecord({
				// 	documentId: uploadDetails?.attachmentId ?? documentId,
				// 	conditionId,
				// 	conditionName,
				// });

				// if (!attached) {
				// 	throw new Error("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED");
				// }

				const attachDocumentResponse = await attachDocumentCondition({
					loanGuid,
					data: {
						condition_mapping: [
							{
								document_id: efolderId,
								attachment_id: efolderId,
								condition_id: conditionId,
								condition_type: conditionType,
								attachment_ids: efolderId,
							},
						],
					},
				}).unwrap();

				const conditionAttachItems = attachDocumentResponse?.data ?? [];

				if (conditionAttachItems.some(isRetryItem)) {
					handleOpenCommonErrorModal?.("DOCUMENT_RETRY_PENDING");
					throw createRetryHandledError();
				}

				if (
					conditionAttachItems.length > 0 &&
					conditionAttachItems.every((item) => item?.status === "success")
				) {
					continue;
				}

				throw new Error("ENHANCED_CONDITION_DOCUMENT_ASSIGNMENT_FAILED");
			}


			await drawerData?.onRefreshDocumentsList?.();
			await waitForPendingApiRequestsToSettle(store);
			handleOpenSuccessModal?.();
			setSuccessText?.("Conditions have been linked");

			setSelectedConditionTypes([]);
		} catch (err) {
			if (isConflictError(err)) {
				if (!retryContext.file) {
					try {
						retryContext = {
							...retryContext,
							file: await buildUploadFilesFromSelectedDocument(row),
						};
					} catch (fileError) {
						handleErrorModal(
							"ENHANCED_CONDITION_DOCUMENT_UPLOAD_FAILED",
							fileError
						);
					}
				}

				const fallbackResponse =
					await retryConflictWithS3Fallback(retryContext);

				if (fallbackResponse) {
					openUploadSuccessModalOnce(LOAN_IN_USE_FALLBACK_MESSAGE);
				}

				throw createRetryHandledError();
			}
			if (!isRetryHandledError(err)) {
				handleErrorModal("DOCUMENT_UPLOAD_ERROR", err);
			}
		} finally {
			handleCloseProcessingModal?.();
		}
	};

	const detailTabs = [
		{ label: "Overview", value: 0 },
		{ label: "eFolder Details", value: 1 },
		{ label: `Conditions (${visibleConditionCount})`, value: 2 },
	];

	const firstIncompleteStatusIndex = pipelineDotColors.findIndex(
		(color) => color === "#D9DDE3"
	);
	const statusSteps = [
		"Still Uploading",
		" Uploaded",
		"Indexed",
		"Condition Matched",
	].map((label, index) => {
		const color = pipelineDotColors[index] ?? "#D9DDE3";
		const completed = color !== "#D9DDE3";
		const active = !completed && firstIncompleteStatusIndex === index;

		return {
			label,
			color,
			completed,
			active,
			count: index + 1,
		};
	});

	const renderDetailCard = (title, rows) => (
		<Box className={styles.sectionBlock}>
			<Heading5
				text={title}
				fontType="semibold"
				extraClass={styles.sectionTitle}
			/>
			<Box className={styles.detailCard}>
				{rows.map((rowItem, index) => (
					<React.Fragment key={rowItem.label}>
						{index > 0 ? <Divider className={styles.cardDivider} /> : null}
						<DetailRow
							label={rowItem.label}
							value={rowItem.value}
							valueClassName={rowItem.valueClassName}
						/>
					</React.Fragment>
				))}
			</Box>
		</Box>
	);

	const renderConditionCards = (documents = []) => {
		//one document can have multiple conditions, so we are iterating over conditions of each document to render condition cards
		if (!documents.length) {
			return (
				<Box className={styles.emptyState}>
					<Body2 text="No matching conditions found." />
				</Box>
			);
		}
		return documents.map((item, index) => {
			return item.conditions.map((condition) => {
				const title = condition?.entityName;
				const conditionId = getConditionIdentifier(condition);
				const reference = conditionId;
				const matchedConditionRow = (
					Array.isArray(conditionRows) ? conditionRows : []
				).find(
					(row) => String(getConditionIdentifier(row)) === String(reference)
				);
				const matchedConditionType =
					matchedConditionRow?.conditionType ??
					matchedConditionRow?.title ??
					matchedConditionRow?.label ??
					matchedConditionRow?.name ??
					condition?.entityName ??
					"-";
				const matchedConditionStatus = String(
					matchedConditionRow?.sub_status ?? matchedConditionRow?.status ?? ""
				)
					.trim()
					.toLowerCase();
				const isClearedOrRejected = ["cleared", "rejected"].includes(
					matchedConditionStatus
				);
				const tone = getConditionStatusTone(condition?.documentStatus);
				const statusText =
					condition?.documentStatus ??
					(tone === "success" ? "Fulfilled" : "Open");

				return (
					<Box className={styles.conditionCard} key={conditionId}>
						<Stack
							direction="column"
							gap={0.25}
							className={styles.conditionCardBody}
						>
							<Body2
								text={title}
								extraClass={styles.conditionTitle}
								fontType="semibold"
							/>

							{/* <Body2 text={reference} extraClass={styles.conditionReference} /> */}
						</Stack>
						{/* <Stack direction="row" alignItems="center" gap={1.25}>
							<StatusPill text={statusText} tone={tone} />
							<Button
								variant="text"
								size="small"
								startIcon={<LinkOffOutlinedIcon />}
								className={styles.unlinkButton}
								disabled={isClearedOrRejected}
								onClick={() =>
									handleUnlinkCondition(condition, matchedConditionType)
								}
							>
								Unlink
							</Button>
						</Stack> */}
					</Box>
				);
			});
		});
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<LoadingDataModal
				open={isLoadingConditionsList}
				text="Loading conditions list..."
			/>
			<LoadingDataModal
				open={isFetchingUploadedBy || isFetchingEncompassFolderDetails}
				text="Fetching details"
			/>
			<Stack direction="column" className={styles.headerContainer}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
				>
					<Stack direction="column" gap={1.25} className={styles.headerCopy}>
						<Stack direction="row" alignItems="center" gap={1}>
							{!hideAttachmentId ? (
								<Box
									className={`${styles.headerPill} ${styles.headerPillMuted}`}
								>
									<Body2
										text={attachmentId}
										extraClass={styles.headerPillText}
									/>
								</Box>
							) : null}
							<Box
								className={`${styles.headerPill} ${styles.headerPillSuccess}`}
							>
								<Body2 text={statusLabel} extraClass={styles.headerPillText} />
							</Box>
						</Stack>

						<Box>
							<Heading4
								text={documentName}
								extraClass={styles.documentTitle}
								fontType="semibold"
							/>
							<Body2
								text={`by ${uploadedBy} - ${uploadedAt}`}
								extraClass={styles.documentMeta}
							/>
						</Box>
					</Stack>

					<IconButton
						onClick={handleCloseDrawer}
						aria-label="Close document tracker detail"
						className={styles.closeButton}
					>
						<CloseIcon className={styles.closeIcon} />
					</IconButton>
				</Stack>
			</Stack>

			<Box className={styles.statusRail}>
				{statusSteps.map((step, index) => {
					const isLast = index === statusSteps.length - 1;
					const lineColor = pipelineDotColors[index] ?? "#D9DDE3";
					const circleColor = step.color ?? "#D9DDE3";

					return (
						<Stack
							key={step.label}
							direction="column"
							alignItems="center"
							className={styles.statusStep}
						>
							<Stack
								direction="row"
								alignItems="center"
								className={styles.statusLineWrap}
							>
								{index > 0 ? (
									<Box
										className={styles.statusLine}
										sx={{ backgroundColor: lineColor }}
									/>
								) : null}
								<Box
									className={styles.statusCircle}
									sx={{
										backgroundColor: circleColor,
										color: "#ffffff",
										boxShadow: step.active
											? "0 0 0 6px rgba(244, 190, 61, 0.18)"
											: "none",
									}}
								>
									{step.completed ? (
										<CheckIcon className={styles.statusCheckIcon} />
									) : (
										<Body2
											text={String(step.count)}
											extraClass={styles.statusCircleNumber}
										/>
									)}
								</Box>
								{!isLast ? (
									<Box
										className={styles.statusLine}
										sx={{ backgroundColor: lineColor }}
									/>
								) : null}
							</Stack>
							<Body2
								text={step.label}
								extraClass={`${styles.statusLabel} ${
									step.completed
										? styles.statusLabelComplete
										: styles.statusLabelActive
								}`}
							/>
						</Stack>
					);
				})}
			</Box>

			<Box className={styles.tabsContainer}>
				<Tabs
					value={tabValue}
					onChange={(_, nextValue) => setTabValue(nextValue)}
					className={styles.tabs}
					variant="scrollable"
					scrollButtons={false}
				>
					{detailTabs.map((tab) => (
						<Tab
							key={tab.label}
							label={tab.label}
							value={tab.value}
							className={styles.tab}
							disableRipple
						/>
					))}
				</Tabs>
			</Box>

			<Box className={styles.contentContainer}>
				{tabValue === 0 ? (
					<Stack direction="column" gap={3}>
						{renderDetailCard(
							"LOGAN CONNECT UPLOAD",
							[
								{ label: "File Name", value: documentName },
								{ label: "File Size", value: fileSize },
								{ label: "Uploaded", value: uploadedAt },
								{ label: "Uploaded By", value: uploadedBy },
								{
									label: "Attachment ID",
									value: attachmentIdDisplayValue,
									valueClassName: styles.inlinePillValue,
								},
								{
									label: "Status",
									value: statusLabel,
									valueClassName: styles.inlinePillValueSuccess,
								},
							].filter(Boolean)
						)}

						{renderDetailCard("ENCOMPASS EFOLDER", [
							{ label: "Document Name", value: encompassFolderDetails?.title },
							{
								label: "eFolder Status",
								value: encompassFolderDetails?.status,
								valueClassName: styles.inlinePillValueSuccess,
							},
							{ label: "Borrower Pair", value: borrowerPair },
						])}

						<Box className={styles.sectionBlock}>
							<Heading5
								text="LINKED UW CONDITIONS"
								fontType="semibold"
								extraClass={styles.sectionTitle}
							/>
							<Stack direction="column" gap={1.25}>
								{renderConditionCards(visibleMatchingConditionDocuments)}
							</Stack>
						</Box>
					</Stack>
				) : tabValue === 1 ? (
					<Stack direction="column" gap={3}>
						{renderDetailCard("ENCOMPASS FOLDER DETAILS", [
							{
								label: "Encompass Document Name",
								value: encompassFolderDetails?.title ?? "-",
							},
							{
								label: "Description",
								value: encompassFolderDetails?.description ?? "-",
							},
							{
								label: "Borrower Pair",
								value: encompassFolderDetails?.application?.entityName ?? "-",
							},
							{
								label: "Status",
								value: encompassFolderDetails?.status ?? "-",
								valueClassName: styles.inlinePillValueSuccess,
							},
							{
								label: "Date",
								value: hasEncompassFolderDetails
									? formatDateTimeEt(encompassFolderDetails?.createdDate)
									: "-",
							},
							{
								label: "Last Attachment",
								value: hasEncompassFolderDetails
									? formatDateTimeEt(encompassFolderDetails?.lastAttachmentDate)
									: "-",
							},
							{
								label: "Size",
								value: hasEncompassFolderDetails
									? (encompassFolderDetails?.documentSize ?? "-")
									: "-",
							},
							{
								label: "Size Total",
								value: hasEncompassFolderDetails
									? (encompassFolderDetails?.totalDocumentSize ?? "-")
									: "-",
							},
						])}
					</Stack>
				) : (
					<Stack direction="column" gap={2}>
						{!isLoadingConditionsList && conditionRows.length > 0 ? (
							<Box className={styles.sectionBlock}>
								<Heading5
									text="Upload Supporting Documentation:"
									fontType="semibold"
									extraClass={styles.sectionTitle}
								/>
								<Box className={styles.conditionTableContainer}>
									<CustomTable
										tableType="document-tracker-conditions"
										squaredBorder={true}
										maxHeight={true}
										rowData={conditionRows}
										setSortedData={() => {}}
										selectedRow={selectedConditionValues}
										setSelectedRow={setSelectedConditionTypes}
									/>
								</Box>
							</Box>
						) : null}
						{!isLoadingConditionsList && conditionRows.length > 0 ? (
							<Stack
								direction="row"
								justifyContent="flex-end"
								alignItems="center"
								gap={1.25}
							>
								<SecondaryButton
									text="Reset"
									onClick={() => setSelectedConditionTypes([])}
									disabled={selectedConditionValues.length === 0}
								/>
								<PrimaryButton
									text="Link Conditions"
									onClick={handleSubmitSelectedConditions}
									disabled={conditionIdsToAttach.length === 0}
								/>
							</Stack>
						) : null}
						<Box className={styles.sectionBlock}>
							<Heading5
								text="Matched Conditions"
								fontType="semibold"
								extraClass={styles.sectionTitle}
							/>
							{renderConditionCards(visibleMatchingConditionDocuments)}
						</Box>
					</Stack>
				)}
			</Box>
		</Stack>
	);
};

export default DocumentTrackerDetailDrawer;
